import { UPDATE_STATE } from '../actions';
import {
  buildCardBackgroundPatchForText,
  findCardBackgroundPartnerId,
  getTextAnnotationLayoutForCardSync,
  isValidTextLayoutMetrics,
} from './textTemplateCardLayout';

const CARD_LAYOUT_EPSILON = 0.5;

const cardLayoutDiffers = (previous, next) =>
  Math.abs((previous?.x ?? 0) - (next?.x ?? 0)) >= CARD_LAYOUT_EPSILON ||
  Math.abs((previous?.y ?? 0) - (next?.y ?? 0)) >= CARD_LAYOUT_EPSILON ||
  Math.abs((previous?.width ?? 0) - (next?.width ?? 0)) >= CARD_LAYOUT_EPSILON ||
  Math.abs((previous?.height ?? 0) - (next?.height ?? 0)) >= CARD_LAYOUT_EPSILON ||
  Math.abs((previous?.rotation ?? 0) - (next?.rotation ?? 0)) >= CARD_LAYOUT_EPSILON;

/** Patch only text + card keys — never spread full annotations (deepMerge + Image nodes overflow). */
const buildAnnotationPatches = (state, textAnnotation, layoutText) => {
  const patches = {
    [textAnnotation.id]: {
      ...state.annotations[textAnnotation.id],
      ...textAnnotation,
    },
  };
  const cardPatch = buildCardBackgroundPatchForText(state.annotations, layoutText);
  if (cardPatch) {
    patches[cardPatch.id] = {
      ...state.annotations[cardPatch.id],
      ...cardPatch,
    };
  }
  return patches;
};

/** One undo step for text + linked sticker card background. */
const dispatchTextWithCardSync = (
  dispatch,
  annotations,
  textAnnotation,
  designLayer,
) => {
  if (!textAnnotation?.id) {
    return;
  }

  const cardId = findCardBackgroundPartnerId(annotations, textAnnotation.id);

  dispatch({
    type: UPDATE_STATE,
    payload: (state) => ({
      isDesignState: true,
      annotations: buildAnnotationPatches(state, textAnnotation, textAnnotation),
    }),
  });

  if (!cardId || !designLayer?.findOne) {
    return;
  }

  // After Konva lays out glyphs (webfonts, center align), snap card to live node bounds.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      dispatch({
        type: UPDATE_STATE,
        payload: (state) => {
          const currentText = state.annotations[textAnnotation.id];
          if (!currentText) {
            return null;
          }
          const layoutText = getTextAnnotationLayoutForCardSync(
            designLayer,
            currentText,
          );
          const cardPatch = buildCardBackgroundPatchForText(
            state.annotations,
            layoutText,
          );
          if (!cardPatch) {
            return null;
          }

          const previousCard = state.annotations[cardPatch.id];
          const nextWidth = layoutText.width ?? 0;
          const nextHeight = layoutText.height ?? 0;
          const layoutMetricsValid = isValidTextLayoutMetrics(
            nextWidth,
            nextHeight,
          );
          const textChanged =
            layoutMetricsValid &&
            (Math.abs((currentText.x ?? 0) - (layoutText.x ?? 0)) >=
              CARD_LAYOUT_EPSILON ||
              Math.abs((currentText.y ?? 0) - (layoutText.y ?? 0)) >=
                CARD_LAYOUT_EPSILON ||
              Math.abs((currentText.width ?? 0) - nextWidth) >=
                CARD_LAYOUT_EPSILON ||
              Math.abs((currentText.height ?? 0) - nextHeight) >=
                CARD_LAYOUT_EPSILON);

          if (!textChanged && !cardLayoutDiffers(previousCard, cardPatch)) {
            return null;
          }

          const patches = {
            [cardPatch.id]: {
              ...previousCard,
              ...cardPatch,
            },
          };

          if (textChanged) {
            patches[textAnnotation.id] = {
              ...currentText,
              x: layoutText.x,
              y: layoutText.y,
              width: layoutText.width,
              height: layoutText.height,
              rotation: layoutText.rotation,
            };
          }

          return {
            isDesignState: false,
            annotations: patches,
          };
        },
      });
    });
  });
};

export default dispatchTextWithCardSync;
